import React, { useState } from 'react';

import { createUniqueKey, getClassNames } from '../../../utils';

import {
    getHeight,
    getXAxis,
    checkData,
    getLabels,
    buildSingleDataArrays,
    getLabelWidth,
    getLegendCoordinates,
} from './utils';

import ChartItem from './ChartItem';
import ChartLabels from './ChartLabels';
import ChartLegend from './ChartLegend';

import withTheme from '../../../hocs/withTheme';

import './BandwidthChart.css';

interface BandwidthChartDataItem {
    name?: string;

    data: {
        curr: number;
        new?: number;
    };

    range: {
        min: number;
        max: number;
    };
}

interface BandwidthChartProps {
    /** Data to be shown on chart */
    data?: BandwidthChartDataItem[];

    /** Width for label of chart (default 150px) */
    labelWidth?: number;

    /** Formats of labels */
    labelFormat?: (name: string) => string;

    /** Formats of numbers (without percent) */
    numberFormat?: (value: number) => string | number;

    /** Label for current value */
    labelCurrent?: string;

    /** Label for new value */
    labelNew?: string;

    className?: string;
}

const BandwidthChart: React.FC<BandwidthChartProps> = ({
    data = [],
    labelNew = '',
    className = '',
    labelWidth = 150,
    labelCurrent = '',
    labelFormat = (name) => name,
    numberFormat = (number) => number,
}) => {
    const hasNew = checkData(data);

    const { height, chartHeight } = getHeight(
        data,
        hasNew
    );

    const [width, setWidth] = useState<number>(0);

    const labels = getLabels(data, labelFormat);

    const labelSize = getLabelWidth(labelWidth, width);

    const dataArray = buildSingleDataArrays(
        data,
        width,
        labels,
        labelSize,
        labelFormat,
        numberFormat
    );

    const xAxis = getXAxis(labelSize, chartHeight);

    const legendCoordinates = getLegendCoordinates(
        width,
        chartHeight,
        labelNew,
        labelCurrent
    );

    const refCallback = (
        element: HTMLDivElement | null
    ) => {
        if (!width && element) {
            setWidth(
                element.getBoundingClientRect().width
            );
        }
    };

    return (
        <div
            className={getClassNames(
                className,
                'BandwidthChart-container'
            )}
            ref={refCallback}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={width}
                height={height}
                version="1.1"
                className="chart-root"
                viewBox={`0 0 ${width} ${height}`}
            >
                <path
                    d={xAxis}
                    className="chart-xaxis-line"
                />

                {dataArray.map((item, index) => (
                    <ChartItem
                        key={createUniqueKey(
                            index,
                            item.label
                        )}
                        data={item}
                        index={index}
                    />
                ))}

                {hasNew && (
                    <ChartLegend
                        data={legendCoordinates}
                        labelNew={labelNew}
                        labelCurrent={labelCurrent}
                    />
                )}
            </svg>

            <ChartLabels labels={labels} />
        </div>
    );
};

export default withTheme(BandwidthChart);